/**
 * Operations · Fulfillment.
 */
import { useMemo, useState } from "react";
import { Truck, PackageCheck, MapPin, AlertOctagon, Sparkles, ClipboardList } from "lucide-react";
import { Card, SectionHeader, TextInput, DeltaChip, Button } from "../../components/lib";
import { TabBar, ListRow, Drawer, Field } from "../../components/lib.overlay";
import { AuditTrail } from "../../components/AuditTrail";
import { FULFILLMENTS, FulfillStage, Fulfillment as FulfillmentT } from "../../mocks/operationsData";

const STAGE_LABEL: Record<FulfillStage, string> = {
  picking: "Picking", packed: "Packed", "handed-to-carrier": "Handed", "in-transit": "In transit",
  delivered: "Delivered", exception: "Exception",
};
const STAGE_META: Record<FulfillStage, { tint: string; fg: string; icon: any }> = {
  picking:              { tint: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)",     icon: ClipboardList  },
  packed:               { tint: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)", icon: PackageCheck   },
  "handed-to-carrier":  { tint: "var(--ph-warning-tint)", fg: "var(--ph-warning)",       icon: Truck          },
  "in-transit":         { tint: "var(--ph-ai-tint)",      fg: "var(--ph-ai)",            icon: Truck          },
  delivered:            { tint: "var(--ph-success-tint)", fg: "var(--ph-success)",       icon: PackageCheck   },
  exception:            { tint: "var(--ph-danger-tint)",  fg: "var(--ph-danger)",        icon: AlertOctagon   },
};

export default function Fulfillment() {
  const [tab, setTab] = useState<FulfillStage | "all">("all");
  const [q, setQ]     = useState("");
  const [selected, setSelected] = useState<FulfillmentT | null>(null);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: FULFILLMENTS.length };
    FULFILLMENTS.forEach((f) => { c[f.stage] = (c[f.stage] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(
    () => FULFILLMENTS.filter((f) =>
      (tab === "all" || f.stage === tab) &&
      (!q || f.orderRef.toLowerCase().includes(q.toLowerCase()) || f.customer.toLowerCase().includes(q.toLowerCase())),
    ),
    [tab, q],
  );

  return (
    <div className="space-y-6" data-testid="op-fulfillment">
      <Card testId="op-ful-summary">
        <SectionHeader eyebrow="Live" title="Shipping pipeline" action={<Truck size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>On-time rate</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-success)" }}>94.3%</div>
            <div className="mt-1"><DeltaChip delta={1.9} trend="up" /></div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>In transit</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>{counts["in-transit"] || 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Exceptions</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: (counts.exception || 0) > 0 ? "var(--ph-danger)" : "var(--ph-ink-strong)" }}>{counts.exception || 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Delivered (30d)</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>{counts.delivered || 0}</div>
          </div>
        </div>
      </Card>

      <Card testId="op-ful-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1"><TextInput value={q} onChange={setQ} placeholder="Search order # or customer…" testId="op-ful-search" /></div>
        </div>
        <div className="mt-4">
          <TabBar<FulfillStage | "all">
            testId="op-ful-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",                label: "All",         count: counts.all               },
              { key: "picking",            label: "Picking",     count: counts.picking           || 0 },
              { key: "packed",             label: "Packed",      count: counts.packed            || 0 },
              { key: "handed-to-carrier",  label: "Handed",      count: counts["handed-to-carrier"] || 0 },
              { key: "in-transit",         label: "In transit",  count: counts["in-transit"]     || 0 },
              { key: "delivered",          label: "Delivered",   count: counts.delivered         || 0 },
              { key: "exception",          label: "Exceptions",  count: counts.exception         || 0 },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="op-ful-list-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Shipments" title={`${filtered.length} shipment${filtered.length === 1 ? "" : "s"}`} />
        </div>
        <div className="p-2" data-testid="op-ful-list">
          {filtered.map((f) => {
            const meta = STAGE_META[f.stage];
            const Icon = meta.icon;
            return (
              <ListRow
                key={f.id}
                testId={`op-ful-${f.id}`}
                onClick={() => setSelected(f)}
                icon={
                  <span className="inline-flex items-center justify-center" style={{
                    width: 28, height: 28, borderRadius: "999px",
                    background: meta.tint, color: meta.fg,
                  }}>
                    <Icon size={13} />
                  </span>
                }
                label={
                  <span className="inline-flex items-center gap-2">
                    <span className="font-mono text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>{f.orderRef}</span>
                    <span>{f.customer}</span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: meta.tint, color: meta.fg, borderRadius: "var(--ph-radius-sm)" }}>
                      {STAGE_LABEL[f.stage]}
                    </span>
                    {f.aiFlag && (
                      <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: "var(--ph-ai)" }}>
                        <Sparkles size={9} /> {f.aiFlag}
                      </span>
                    )}
                  </span>
                }
                sublabel={<><MapPin size={10} className="inline mr-0.5" />{f.city} · {f.carrier} · {f.weightKg}kg · ETA {f.eta}</>}
              />
            );
          })}
        </div>
      </Card>

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Fulfillment 360" testId="op-ful-drawer" width={520}>
        {selected && (() => {
          const meta = STAGE_META[selected.stage];
          const Icon = meta.icon;
          return (
            <div>
              <div className="text-[10px] font-mono" style={{ color: "var(--ph-ink-muted)" }}>{selected.orderRef}</div>
              <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.25rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">{selected.customer}</div>
              <div className="mb-3">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: meta.tint, color: meta.fg, borderRadius: "var(--ph-radius-sm)" }}>
                  <Icon size={10} /> {STAGE_LABEL[selected.stage]}
                </span>
              </div>
              <Field label="Order reference" inline><span className="text-sm font-mono">{selected.orderRef}</span></Field>
              <Field label="Customer"        inline><span className="text-sm">{selected.customer}</span></Field>
              <Field label="Destination"     inline><span className="text-sm"><MapPin size={11} className="inline mr-0.5" />{selected.city}</span></Field>
              <Field label="Carrier"         inline><span className="text-sm">{selected.carrier}</span></Field>
              <Field label="Tracking"        inline><span className="text-sm font-mono">{selected.orderRef}-TRK</span></Field>
              <Field label="Weight"          inline><span className="text-sm tabular-nums">{selected.weightKg} kg</span></Field>
              <Field label="Shipping cost"   inline><span className="text-sm tabular-nums">EGP {Math.round(selected.weightKg * 30)}</span></Field>
              <Field label="ETA"             inline><span className="text-sm">{selected.eta}</span></Field>
              <Field label="Delivery status" inline>
                <span className="text-sm capitalize" style={{ color: selected.stage === "delivered" ? "var(--ph-success)" : selected.stage === "exception" ? "var(--ph-danger)" : "var(--ph-ink-default)" }}>
                  {STAGE_LABEL[selected.stage]}
                </span>
              </Field>
              <Field label="Proof of delivery" inline>
                <span className="text-sm" style={{ color: selected.stage === "delivered" ? "var(--ph-success)" : "var(--ph-ink-muted)" }}>
                  {selected.stage === "delivered" ? "Signed" : "Pending"}
                </span>
              </Field>
              <Field label="Signature"       inline>
                <span className="text-sm">{selected.stage === "delivered" ? `${selected.customer.split(" ")[0]} · verified` : "—"}</span>
              </Field>
              {selected.aiFlag && (
                <div className="pt-3">
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1.5" style={{ color: "var(--ph-ai)" }}>AI flag</div>
                  <div className="text-sm p-3" style={{ background: "var(--ph-ai-tint)", borderRadius: "var(--ph-radius-md)", color: "var(--ph-ink-default)" }}>
                    {selected.aiFlag}
                  </div>
                </div>
              )}
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Timeline</div>
                <AuditTrail collection="fulfillments" recordId={selected.id} testId="op-ful-audit" />
              </div>
              <div className="pt-4 flex justify-end gap-2">
                <Button variant="secondary" testId="op-ful-track">Track shipment</Button>
                <Button testId="op-ful-mark-delivered">Mark delivered</Button>
              </div>
            </div>
          );
        })()}
      </Drawer>
    </div>
  );
}
